/* eslint-disable @typescript-eslint/no-explicit-any */
import { Image, Types, Text, Button, RichText } from '@marriott/mi-ui-library';
import clsx from 'clsx';
import { ButtonStyle, IMAGE_URL_DOMAIN } from '../../../../modules/utils/constants/constants';
import { StyledSignatureItems } from './CarouselContainer.styles';
import { useEffect, useRef, useState, useId } from 'react';
import { useGetBreakpoint } from '../../../../modules/utils/helpers';

export const CarouselContainer = (props: any) => {
  const isImageAvailable =
    ((props?.isMobileViewPort || props?.isTabletViewPort) && props?.imageData?.imageUrls?.classicHorizontal?.trim()) ||
    ((!props?.isMobileViewPort || !props?.isTabletViewPort) && props?.imageData?.imageUrls?.square?.trim());
  const [isTruncated, setIsTruncated] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [isMeasuring, setIsMeasuring] = useState(true);
  const textRef = useRef<HTMLDivElement>(null);
  const uniqueId = useId();
  const breakPoint = useGetBreakpoint();
  const [isMobileViewPort, setIsMobileViewPort] = useState(useGetBreakpoint() === 'mobile');
  const [isTabletViewPort, setIsTabletViewPort] = useState(useGetBreakpoint() === 'tablet');
  const dietList = props?.suitableDiets?.map((obj: any) => obj.name);

  useEffect(() => {
    function handleResize() {
      setIsMobileViewPort(breakPoint === 'mobile');
      setIsTabletViewPort(breakPoint === 'tablet');
    }

    const checkTextHeight = () => {
      const container = textRef.current;
      if (!container) return;

      // Find the RichText component within THIS instance's container
      // Each instance has its own container, so querySelector will only find elements within this instance
      // RichText wrapper has id={componentId}, so we can find it by id attribute
      const richTextElement = container.querySelector(
        `[id="carousel-container-description-${uniqueId}"]`
      ) as HTMLElement | null;
      if (!richTextElement) return;

      // Find the inner content div that contains the actual rendered text
      // The RichText component structure: div[data-testid] > div > div[contentEditable]
      const contentDiv = richTextElement.querySelector('div > div') as HTMLElement | null;
      if (!contentDiv) return;

      // Get computed styles from the RichText wrapper
      const style = window.getComputedStyle(richTextElement);
      const lineHeight = parseFloat(style.lineHeight);

      if (!lineHeight || isNaN(lineHeight) || lineHeight === 0) {
        setShowButton(false);
        setIsTruncated(false);
        setIsMeasuring(false);
        return;
      }

      // Measure the full height of the content (scrollHeight gives us the full content height)
      // We measure when it's in full-description mode (not truncated)
      const fullHeight = contentDiv.scrollHeight;
      const maxHeightFor8Lines = lineHeight * 8;

      // Only show button if content exceeds 8 lines
      if (fullHeight > maxHeightFor8Lines) {
        setShowButton(true);
        setIsTruncated(true);
      } else {
        setShowButton(false);
        setIsTruncated(false);
      }

      setIsMeasuring(false);
    };

    // Reset measuring state when description changes
    setIsMeasuring(true);

    // Use requestAnimationFrame to ensure DOM is fully rendered
    const rafId = requestAnimationFrame(() => {
      // Use setTimeout to ensure styles are applied
      setTimeout(() => {
        checkTextHeight();
      }, 0);
    });

    handleResize();

    const handleResizeWithCheck = () => {
      handleResize();
      setIsMeasuring(true);
      requestAnimationFrame(() => {
        setTimeout(checkTextHeight, 0);
      });
    };

    window.addEventListener('resize', handleResizeWithCheck);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResizeWithCheck);
    };
  }, [breakPoint, props.description]);

  const getButtonStyle = (ctaType: string): string => {
    switch (ctaType) {
      case 'primaryButton':
        return ButtonStyle.PRIMARY_BUTTON;
      case 'secondaryButton':
        return ButtonStyle.SECONDARY_BUTTON;
      case 'tertiaryLink':
        return ButtonStyle.TERTIARY_BUTTON;
      default:
        return '';
    }
  };

  const toggleTruncate = () => {
    setIsTruncated(!isTruncated);
  };
  return (
    <StyledSignatureItems
      className={clsx(
        'signature-items__container d-flex align-items-center',
        !isImageAvailable && 'justify-content-center m-0 my-auto'
      )}
    >
      {(((props?.isMobileViewPort || props.isTabletViewPort) && props?.imageData?.imageUrls?.classicHorizontal) ||
        ((!props?.isMobileViewPort || !props.isTabletViewPort) && props?.imageData?.imageUrls?.square)) && (
        <div className="carousel-img-container p-0">
          <Image
            defaultImageURL={
              props?.isMobileViewPort || props?.isTabletViewPort
                ? `${IMAGE_URL_DOMAIN}${props?.imageData?.imageUrls?.classicHorizontal}`
                : `${IMAGE_URL_DOMAIN}${props?.imageData?.imageUrls?.square}`
            }
            altText={props?.imageData?.title}
          />
        </div>
      )}
      <div
        className={clsx(
          'signature-items__content-container',
          !isMobileViewPort && !isTabletViewPort && !isImageAvailable ? 'item-without-image' : ''
        )}
      >
        <div
          className={clsx(
            'signature-items__content-container__content d-flex justify-content-md-center align-items-center flex-column text-wrap py-4 px-md-0',
            isImageAvailable
              ? 'justify-content-start align-items-lg-start'
              : 'justify-content-center align-items-lg-center',
            props?.componentType === 'menu-signature-item' ? '' : 'mr-0',
            !isImageAvailable ? 'mx-0' : ''
          )}
        >
          {props?.eyebrowText && (
            <Text
              copyText={props?.eyebrowText}
              fontSize={Types.size.medium}
              element={Types.tags.paragraph}
              customClass={'t-overline-alt-normal mb-3'}
            />
          )}
          {props?.header && (
            <Text
              copyText={props?.header}
              fontSize={Types.size.large}
              element={Types.tags.paragraph}
              customClass={clsx('t-title-l mb-3 text-center', !isImageAvailable ? '' : 'text-md-left')}
            />
          )}
          {props?.locationInfo && (
            <div
              className={clsx(
                'location-info d-flex py-3 py-md-1 mb-3 align-items-start',
                isMobileViewPort || isTabletViewPort || !isImageAvailable
                  ? 'justify-content-center'
                  : 'justify-content-start'
              )}
            >
              {props?.iconPath && <span className={clsx('icon-s mr-1', props?.iconPath)}></span>}
              <span className="t-font-m">{props?.locationInfo}</span>
            </div>
          )}

          {props?.description && (
            <div ref={textRef} id="description-text">
              <RichText
                componentId={`carousel-container-description-${uniqueId}`}
                customClass={clsx(
                  // Always render in full mode during measurement, then apply truncation if needed
                  isMeasuring || !isTruncated ? 'full-description' : 'truncated-description',
                  't-font-m mb-3 text-center',
                  !isImageAvailable ? 'text-center' : 'text-md-left'
                )}
                text={props?.description}
              />
              <div className={clsx(isMobileViewPort || isTabletViewPort ? 'd-flex justify-content-center' : '')}>
                {showButton && (
                  <Button
                    type={Types.ButtonTypeVariation.Button}
                    className={clsx('show-more-button py-2 px-0 m-0 mt-2 t-font-m')}
                    callback={toggleTruncate}
                  >
                    <span className={clsx(' t-subtitle-m')}>
                      {isTruncated ? props?.showMoreLabel || '' : props?.showLessLabel || ''}
                    </span>
                    <span
                      className={clsx('ml-1 show-more-icon', isTruncated ? 'icon-dropdown-down' : 'icon-dropdown-up')}
                    ></span>
                  </Button>
                )}
              </div>
            </div>
          )}
          {dietList && dietList?.length > 0 && (
            <Text
              copyText={dietList?.join(', ')}
              fontSize={Types.size.medium}
              element={Types.tags.paragraph}
              customClass={'t-overline-alt-normal mb-3'}
            />
          )}
          {props?.priceWithCurrency && (
            <div>
              <span className="t-subtitle-xl">{props?.priceWithCurrency?.split(/[a-zA-Z]+/)[1]}</span>{' '}
              <span className="t-font-xs">{props?.priceWithCurrency?.split(/\d+/)[0]}</span>
            </div>
          )}
          {(props?.primaryCtaLink || props?.secondaryCtaLink) && (
            <div className="d-flex pt-4">
              {props?.primaryCtaLink && (
                <Button
                  isLink
                  href={props?.primaryCtaLink}
                  className={clsx(`m-button-m`, getButtonStyle(props?.primaryCtaType))}
                  target={props?.primaryCtaOpenInNewTab ? '_blank' : ''}
                  ariaLabel={props?.primaryCtaLabel}
                  custom_click_track_value={`${props?.customCLickTrackDataLoc}|${props?.primaryCtaLabel}|${props?.customCLickTrackLinkType}`}
                >
                  {props?.primaryCtaLabel}
                </Button>
              )}
              {props?.secondaryCtaLink && (
                <Button
                  isLink
                  href={props?.secondaryCtaLink}
                  className={clsx(`ml-3 m-button-m`, getButtonStyle(props?.secondaryCtaType))}
                  target={props?.secondaryCtaOpenInNewTab ? '_blank' : ''}
                  ariaLabel={props?.secondaryCtaLabel}
                  custom_click_track_value={`${props?.customCLickTrackDataLoc}|${props?.secondaryCtaLabel}|${props?.customCLickTrackLinkType}`}
                >
                  {props?.secondaryCtaLabel}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </StyledSignatureItems>
  );
};
